import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import ParabolaShape from "../../../../components/visuals/ParabolaShape";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

function simplifyFrac(num, den) {
  if (den < 0) return simplifyFrac(-num, -den);
  const g = gcd(num, den);
  return [num / g, den / g];
}

function fracToLatex(num, den) {
  const [n, d] = simplifyFrac(num, den);
  if (d === 1) return `${n}`;
  return String.raw`\frac{${n}}{${d}}`;
}

function numberToLatex(v) {
  if (Number.isInteger(v)) return `${v}`;
  const rounded = Math.round(v * 2);
  if (Math.abs(rounded / 2 - v) < 1e-9) {
    return fracToLatex(rounded, 2);
  }
  return `${v}`;
}

function parseNumber(rawValue) {
  if (rawValue == null) return null;
  const raw =
    typeof rawValue === "object" && rawValue.raw != null
      ? String(rawValue.raw)
      : String(rawValue);
  const s = raw.trim().replace(/\s+/g, "").replace(",", ".").replace(/\u2212/g, "-");
  if (!s) return null;
  const frac = s.match(/^([+-]?\d+)\/([+-]?\d+)$/);
  if (frac) {
    const p = Number(frac[1]);
    const q = Number(frac[2]);
    if (!Number.isFinite(p) || !Number.isFinite(q) || q === 0) return null;
    return p / q;
  }
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
}

function eq(a, b, tol = 1e-9) {
  if (a === b) return true;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= tol;
}

function sameInterval(user, expected) {
  if (!user || !expected) return false;
  return (
    eq(user.left, expected.left) &&
    eq(user.right, expected.right) &&
    user.leftClosed === expected.leftClosed &&
    user.rightClosed === expected.rightClosed
  );
}

export function generateTask() {
  const k = randInt(2, 5);
  const xStar = randInt(2, 6);

  // T = S/4. Dobor T zapewnia calkowite x* i "ladny" koniec dziedziny.
  const T = (3 * (1 + k) * xStar) / 2;
  const S = 4 * T; // suma wszystkich krawedzi

  const rightDomain = T / (1 + k); // = 3*xStar/2
  const hStar = T - (1 + k) * xStar; // dodatnie
  const vMax = k * xStar * xStar * hStar;

  return {
    type: "CuboidEdgeSumOptimizationTask",
    prompt: `Dany jest prostopadłościan: jedna krawędź podstawy jest ${k} razy dłuższa od drugiej, a suma wszystkich krawędzi wynosi ${S} cm. Wyznacz dziedzinę x, x dla maksimum objętości oraz wartość maksymalną objętości.`,
    data: {
      k,
      S,
      T,
      rightDomain,
      xStar,
      hStar,
      vMax,
      expectedDomain: {
        left: 0,
        right: rightDomain,
        leftClosed: false,
        rightClosed: false,
      },
      expectedDomainLatex: `(0;\\,${numberToLatex(rightDomain)})`,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>
        Dany jest prostopadłościan, w którym jedna krawędź podstawy jest{" "}
        <InlineMath math={`${data.k}`} /> razy dłuższa od drugiej.
      </p>
      <p>
        Suma długości wszystkich krawędzi wynosi{" "}
        <InlineMath math={`${data.S}\\,\\text{cm}`} />.
      </p>
      <p>
        Wyznacz:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>dziedzinę zmiennej <InlineMath math={"x"} />,</li>
        <li>wartość <InlineMath math={"x"} />, dla której objętość jest największa,</li>
        <li>największą objętość.</li>
      </ul>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: 6 }}>
          <strong>Dziedzina:</strong>
        </div>
        <IntervalInput
          value={ua.domain ?? ""}
          onChange={(next) => onChange({ ...ua, domain: next })}
          disabled={disabled}
          asString
          previewWithX
          prefixAbove
          placeholder="np. (0; 9/2)"
        />
      </div>

      <div>
        <div style={{ marginBottom: 6 }}>
          <InlineMath math={"x_{max}"} /> =
        </div>
        <FractionInput
          value={ua.xStar}
          onChange={(v) => onChange({ ...ua, xStar: v })}
          disabled={disabled}
          label={"x_{max}"}
          suffixLatex={"\\text{cm}"}
        />
      </div>

      <div>
        <div style={{ marginBottom: 6 }}>
          <InlineMath math={"V_{max}"} /> =
        </div>
        <FractionInput
          value={ua.vMax}
          onChange={(v) => onChange({ ...ua, vMax: v })}
          disabled={disabled}
          label={"V_{max}"}
          suffixLatex={"\\text{cm}^3"}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const rawDomain =
    typeof userAnswer?.domain === "string"
      ? userAnswer.domain
      : userAnswer?.domain?.raw || "";

  const parsed = parseIntervalInput(rawDomain);
  const domainOk =
    parsed &&
    parsed.kind === "intervals" &&
    parsed.intervals.length === 1 &&
    sameInterval(parsed.intervals[0], data.expectedDomain);

  const xVal = parseNumber(userAnswer?.xStar);
  const vVal = parseNumber(userAnswer?.vMax);

  const xOk = xVal != null && Math.abs(xVal - data.xStar) < 1e-9;
  const vOk = vVal != null && Math.abs(vVal - data.vMax) < 1e-9;

  return Boolean(domainOk && xOk && vOk);
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const ua = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  if (!ua) return <i>(brak odpowiedzi)</i>;

  const rawDomain =
    typeof ua.domain === "string" ? ua.domain : ua.domain?.raw || "";
  const dLatex = rawDomain.trim()
    ? intervalToLatex(rawDomain, { withX: true })
    : null;

  const xRaw = ua.xStar?.raw ?? ua.xStar ?? "";
  const vRaw = ua.vMax?.raw ?? ua.vMax ?? "";
  const xCore = xRaw ? formatLatexPreview(String(xRaw), "x_{max}") : null;
  const xLatex = xCore ? `${xCore}\\,\\text{cm}` : null;
  const vCore = vRaw ? formatLatexPreview(String(vRaw), "V_{max}") : null;
  const vLatex = vCore ? `${vCore}\\,\\text{cm}^3` : null;

  if (!dLatex && !xLatex && !vLatex) return <i>(brak odpowiedzi)</i>;

  return (
    <div className="space-y-1">
      {dLatex ? <div><InlineMath math={dLatex} /></div> : null}
      {xLatex ? <div><InlineMath math={xLatex} /></div> : null}
      {vLatex ? <div><InlineMath math={vLatex} /></div> : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div className="space-y-1">
      <div>
        <InlineMath math={`x\\in ${data.expectedDomainLatex}`} />
      </div>
      <div>
        <InlineMath math={`x_{max}=${numberToLatex(data.xStar)}\\,\\text{cm}`} />
      </div>
      <div>
        <InlineMath math={`V_{max}=${numberToLatex(data.vMax)}\\,\\text{cm}^3`} />
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const k = data.k;
  const T = data.T;
  const rightL = numberToLatex(data.rightDomain);
  const xStarL = numberToLatex(data.xStar);
  const vMaxL = numberToLatex(data.vMax);
  const c3 = k * (1 + k);
  const c2 = k * T;

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Zapisujemy zależności między wymiarami.</p>
      <BlockMath math={`a=x,\\quad b=${k}x,\\quad h`} />
      <BlockMath math={`4(a+b+h)=${data.S}`} />
      <BlockMath math={`a+b+h=${T}`} />
      <BlockMath math={`x+${k}x+h=${T}`} />
      <BlockMath math={`h=${T}-${1 + k}x`} />

      <p><strong>Krok 2:</strong> Wyznaczamy dziedzinę.</p>
      <BlockMath math={"x>0"} />
      <BlockMath math={"h>0"} />
      <BlockMath math={`h=${T}-${1 + k}x\\Rightarrow ${T}-${1 + k}x>0`} />
      <BlockMath math={`-${1 + k}x>-${T}`} />
      <BlockMath math={`x<\\frac{${T}}{${1 + k}}`} />
      <BlockMath math={`\\text{Lacznie: }x>0\\ \\land\\ x<\\frac{${T}}{${1 + k}}`} />
      <BlockMath math={`x\\in (0;\\,${rightL})`} />

      <p><strong>Krok 3:</strong> Budujemy funkcję objętości i upraszczamy do wielomianu.</p>
      <BlockMath math={`V(x)=a\\cdot b\\cdot h=x\\cdot ${k}x\\cdot\\left(${T}-${1 + k}x\\right)`} />
      <BlockMath math={`V(x)=${k}x^2\\left(${T}-${1 + k}x\\right)`} />
      <BlockMath math={`V(x)=-${c3}x^3+${c2}x^2`} />

      <p><strong>Krok 4:</strong> Liczymy pochodną i punkt krytyczny.</p>
      <BlockMath math={`V'(x)=-${3 * c3}x^2+${2 * c2}x`} />
      <BlockMath math={`V'(x)=${k}x\\left(${2 * T}-${3 * (1 + k)}x\\right)`} />
      <BlockMath math={`V'(x)=0\\Rightarrow x=0\\ \\text{lub}\\ x=\\frac{${2 * T}}{${3 * (1 + k)}}=${xStarL}`} />
      <p><strong>Krok 5:</strong> Badamy znak pochodnej (szkic paraboli <InlineMath math={"V'(x)"} />).</p>
      <BlockMath math={`\\text{W dziedzinie }x>0\\text{, wiec znak }V'(x)\\text{ zalezy od }${2 * T}-${3 * (1 + k)}x.`} />
      <BlockMath math={`x<${xStarL}\\Rightarrow V'(x)>0,\\qquad x>${xStarL}\\Rightarrow V'(x)<0`} />
      <div style={{ maxWidth: 560, margin: "20px auto" }}>
        <ParabolaShape
          aSign={-1}
          rootMode="two"
          rootLeft={-2}
          rootRight={2}
          labelLeft={String(0)}
          labelRight={String(data.xStar)}
          xRange={5}
          yWindow={5}
          showXAxis
          showZeroTicks
          curveColor="#f59e0b"
          width={560}
          height={200}
        />
      </div>
      <p>
        Pochodna zmienia znak z <InlineMath math={"+"} /> na <InlineMath math={"-"} />,
        więc dla <InlineMath math={`x=${xStarL}`} /> funkcja <InlineMath math={"V(x)"} /> ma maksimum.
      </p>

      <p><strong>Krok 6:</strong> Liczymy największą objętość.</p>
      <BlockMath math={`V_{max}=V(${xStarL})=${vMaxL}\\,\\text{cm}^3`} />
    </div>
  );
}
